import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle'
import RefreshCw from 'lucide-react/dist/esm/icons/refresh-cw'

import {
  PortalErrorHelpFooter,
  PortalStatusPageShell,
  PortalStatusWordmark,
  elevatedCardClassName,
} from '@/components/layout/portalStatusPage'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/lib/utils'

interface ErrorStateProps {
  title: string
  description: string
  actionLabel?: string
  onAction?: () => void
  secondaryActionLabel?: string
  onSecondaryAction?: () => void
  variant?: 'page' | 'inline'
  className?: string
}

export function ErrorState({
  title,
  description,
  actionLabel = 'Try again',
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  variant = 'inline',
  className,
}: ErrorStateProps) {
  const icon = (
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-destructive/10">
      <AlertCircle className="h-6 w-6 text-destructive" aria-hidden="true" />
    </span>
  )

  if (variant === 'page') {
    return (
      <PortalStatusPageShell>
        <Card className={cn(elevatedCardClassName, className)}>
          <div className="border-b border-border/30 px-8 pb-5 pt-9 text-center">
            <PortalStatusWordmark />

            <div className="mt-7 space-y-5">
              <div className="inline-flex items-center gap-2 rounded-full border border-destructive/20 bg-destructive/5 px-3.5 py-1.5 text-xs font-semibold text-foreground">
                <AlertCircle className="h-4 w-4 text-destructive" aria-hidden="true" />
                Page error
              </div>

              <div className="space-y-2.5">
                <h1 className="text-xl font-semibold tracking-tight text-foreground">{title}</h1>
                <p className="text-sm leading-relaxed text-muted-foreground">{description}</p>
              </div>
            </div>
          </div>

          {(onAction || onSecondaryAction) && (
            <CardContent className="space-y-3 px-8 pb-8 pt-6">
              {onAction && (
                <Button
                  type="button"
                  onClick={onAction}
                  className="h-12 w-full rounded-xl text-base font-semibold"
                >
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                  {actionLabel}
                </Button>
              )}
              {onSecondaryAction && secondaryActionLabel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onSecondaryAction}
                  className="h-12 w-full rounded-xl text-base font-semibold"
                >
                  {secondaryActionLabel}
                </Button>
              )}
            </CardContent>
          )}
        </Card>

        <PortalErrorHelpFooter />
      </PortalStatusPageShell>
    )
  }

  return (
    <div
      className={cn(
        'flex flex-col items-center rounded-2xl border border-destructive/30 bg-destructive/5 px-6 py-10 text-center',
        className,
      )}
    >
      {icon}
      <div className="mt-4 space-y-2">
        <h2 className="text-sm font-medium text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      {(onAction || onSecondaryAction) && (
        <div className="mt-5 flex w-full flex-col items-center justify-center gap-2 sm:flex-row">
          {onAction && (
            <Button type="button" onClick={onAction} className="rounded-xl">
              <RefreshCw className="h-4 w-4" aria-hidden="true" />
              {actionLabel}
            </Button>
          )}
          {onSecondaryAction && secondaryActionLabel && (
            <Button type="button" variant="outline" onClick={onSecondaryAction} className="rounded-xl">
              {secondaryActionLabel}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

/** Default full-page fallback for uncaught render errors (Sentry boundary, app ErrorBoundary). */
export function PageErrorFallback() {
  return (
    <ErrorState
      variant="page"
      title="Something went wrong"
      description="The page could not load properly, your uploads are safe, try refreshing or sign in again"
      actionLabel="Refresh page"
      onAction={() => window.location.reload()}
      secondaryActionLabel="Back to sign in"
      onSecondaryAction={() => {
        window.location.href = '/login'
      }}
    />
  )
}
